import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import React, { Component } from 'react';
import { getCurrentSeason } from '../../../selectors/seasonSelectors';
import CropWaterEventCard from '../../../components/cards/CropWaterUse/CropWaterEventCard';
import CropWaterUseHeader from '../../../components/cards/CropWaterUse/CropWaterUseHeader';
import CropWaterUsePreview from '../../../components/cards/CropWaterUse/CropWaterUsePreview';
import { PanelCards } from '../../Panel';
import styles from './CropWaterPanel.css';
import { setActiveSubLayer } from '../../../actions/layersActions';

import { CROP_WATER_KEY } from '../../../constants/mapLayerIds';
import { HARVEST, PLANTING, WEATHER } from '../../../constants/cards';
import { AppState } from '../../../store/types/Store';

const CARDS = [WEATHER, PLANTING, HARVEST];

interface CropWaterEvent {
  avgCropWaterUse: { q: number; u: string };
  thumbnail: string;
  [key: string]: unknown;
}

interface OwnProps {
  index: number;
  isCollapsed: boolean;
  isSplitViewEnabled: boolean;
  collapsePanel: () => void;
}

interface StateProps {
  activeSeason: string;
  currentDate: string;
  dates: string[];
  events: Record<string, CropWaterEvent>;
  locale: string;
}

interface DispatchProps {
  setActiveSubLayer: typeof setActiveSubLayer;
}

type CropWaterPanelProps = OwnProps & StateProps & DispatchProps;

export class CropWaterPanel extends Component<CropWaterPanelProps> {
  handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
    const {
      setActiveSubLayer: setActiveSubLayerProp,
      collapsePanel,
      index,
      activeSeason,
    } = this.props;
    setActiveSubLayerProp(
      CROP_WATER_KEY,
      e.currentTarget.id,
      index,
      activeSeason
    );

    collapsePanel();
  };

  onLeftClickPreview = () => {
    const {
      dates,
      currentDate,
      setActiveSubLayer: setActiveSubLayerProp,
      activeSeason,
      index: indexProp,
    } = this.props;
    if (!dates?.length) return;
    const currentIndex = dates.indexOf(currentDate);
    const index = currentIndex < dates.length - 1 ? currentIndex + 1 : 0;

    setActiveSubLayerProp(
      CROP_WATER_KEY,
      dates[index],
      indexProp,
      activeSeason
    );
  };

  onRightClickPreview = () => {
    const {
      dates,
      activeSeason,
      currentDate,
      setActiveSubLayer: setActiveSubLayerProp,
      index: indexProp,
    } = this.props;
    if (!dates?.length) return;
    const currentIndex = dates.indexOf(currentDate);
    const index = currentIndex > 0 ? currentIndex - 1 : dates.length - 1;

    setActiveSubLayerProp(
      CROP_WATER_KEY,
      dates[index],
      indexProp,
      activeSeason
    );
  };

  mapDates = (date: string, eventIndex: number) => {
    const { events, currentDate, locale } = this.props;
    const event = events[date];
    if (!event) return null;
    return (
      <CropWaterEventCard
        averageCropWaterUse={event.avgCropWaterUse}
        date={date}
        eventIndex={eventIndex}
        id={date}
        imageUrl={event.thumbnail}
        isActive={currentDate === date}
        key={date}
        locale={locale}
        onClick={this.handleCardClick}
      />
    );
  };

  getFirstCard = () => {
    const {
      dates,
      isSplitViewEnabled,
      isCollapsed,
      currentDate,
      events,
      locale,
    } = this.props;

    if (!dates?.length || !events) return undefined;

    const currentEvent = events[currentDate];

    return (
      <div className={styles.panel}>
        {isSplitViewEnabled && isCollapsed && currentEvent && (
          <CropWaterUsePreview
            date={currentDate}
            locale={locale}
            onLeftClick={this.onLeftClickPreview}
            onRightClick={this.onRightClickPreview}
            precipitation={currentEvent.avgCropWaterUse}
          />
        )}
        <CropWaterUseHeader isSplitViewEnabled={isSplitViewEnabled} />
        <div className={styles.events}>{dates.map(this.mapDates)}</div>
      </div>
    );
  };

  render() {
    const { isCollapsed, index, dates, events } = this.props;

    if (!dates || !events) {
      return (
        <PanelCards
          cards={CARDS}
          index={index}
          isCollapsed={isCollapsed}
          testId="cropwateruse"
        />
      );
    }

    return (
      <PanelCards
        cards={CARDS}
        firstCard={this.getFirstCard()}
        index={index}
        isCollapsed={isCollapsed}
        testId="cropwateruse"
      />
    );
  }
}

function mapStateToProps(state: AppState, props: OwnProps): StateProps {
  const activeSeason = getCurrentSeason(state, props);
  const layerData = state.layers.list?.[activeSeason]?.[CROP_WATER_KEY] as {
    byId: Record<string, CropWaterEvent>;
    allIds: string[];
    current: string[];
  } | undefined;

  if (!layerData) {
    return {
      activeSeason,
      currentDate: '',
      dates: [],
      events: {},
      locale: state.app.locale,
    };
  }

  const { byId, allIds, current } = layerData;

  return {
    activeSeason,
    currentDate: current[props.index],
    dates: allIds,
    events: byId,
    locale: state.app.locale,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return bindActionCreators({ setActiveSubLayer }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(CropWaterPanel);
